import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SocketService } from '../socket/socket.service';
import { AttendanceCopilotStateService } from './attendance-copilot-state.service';

export interface CopilotSocketPayload {
  /** The attendance the event is about — the field the gateway's envelope carries. */
  conversationId: string;
  requestId: string;
  action: string;
  status: 'success' | 'error';
  result?: Record<string, unknown>;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class AttendanceCopilotSocketService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private currentChannel: string | null = null;
  private subscriptions: Subscription[] = [];

  private readonly COPILOT_EVENTS = [
    'copilot:suggestion',
    'copilot:playbook',
    'copilot:health',
    'copilot:sentiment',
    'copilot:ask',
    'copilot:error',
    'copilot:status',
  ] as const;

  constructor(
    private readonly socketService: SocketService,
    private readonly stateService: AttendanceCopilotStateService,
  ) {}

  joinAttendance(idAttendance: string): void {
    this.leaveCurrentChannel();

    this.currentChannel = `copilot_attendance_${idAttendance}`;
    this.socketService.joinEvent(this.currentChannel);

    for (const event of this.COPILOT_EVENTS) {
      const sub = this.socketService
        .listen(event)
        .pipe(takeUntil(this.destroy$))
        .subscribe((payload: CopilotSocketPayload) => this.handlePayload(payload));
      this.subscriptions.push(sub);
    }

    this.stateService.setSocketConnected(true);
  }

  leaveCurrentChannel(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
    this.currentChannel = null;
    this.stateService.setSocketConnected(false);
  }

  private handlePayload(payload: CopilotSocketPayload): void {
    if (!payload?.requestId) return;

    if (payload.status === 'success' && payload.result) {
      this.stateService.resolveCopilotMessage(payload.requestId, payload.result);
    } else if (payload.status === 'error') {
      this.stateService.rejectCopilotMessage(
        payload.requestId,
        payload.message ?? '',
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
